import { Command } from 'cmdk';
import { Check, ChevronDown, ChevronsUpDown, Loader2, Plus, X } from 'lucide-react';
import { Select as S } from 'radix-ui';
import { type ReactNode, useState } from 'react';
import { cn } from '../lib/cn';
import { controlBox, controlInvalid, useControlProps } from './field';
import { Popover, PopoverContent, PopoverTrigger } from './overlay';

export interface Option {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

const triggerBase = cn(
  controlBox,
  'h-(--control-h) justify-between gap-2 px-3 text-left',
  'data-[placeholder]:text-subtle hover:bg-surface-2 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-disabled',
);

/* ------------------------------------------------------------------- Select */

export interface SelectProps {
  options: Option[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

/** Simple dropdown for short, fixed lists (severity, side, injury context). Use Combobox when the list is long or searchable. */
export function Select({ options, value, onValueChange, placeholder = 'Select…', disabled, invalid, id, className, ...aria }: SelectProps) {
  const control = useControlProps({ id, invalid });
  return (
    <S.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <S.Trigger
        id={control.id}
        aria-describedby={control['aria-describedby']}
        aria-invalid={control['aria-invalid']}
        aria-required={control['aria-required']}
        className={cn(triggerBase, control.invalid && controlInvalid, className)}
        {...aria}
      >
        <S.Value placeholder={placeholder} />
        <S.Icon>
          <ChevronDown className="size-4 text-subtle" aria-hidden />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          className="z-[60] max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border bg-surface shadow-lg data-[state=open]:animate-slide-down"
        >
          <S.Viewport className="p-1">
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex min-h-9 cursor-default select-none items-center gap-2 rounded-md py-1.5 pl-8 pr-3 text-base text-fg outline-none data-[disabled]:opacity-50 data-[highlighted]:bg-sunken"
              >
                <span className="absolute left-2.5 grid size-4 place-items-center">
                  <S.ItemIndicator>
                    <Check className="size-4 text-brand-text" aria-hidden />
                  </S.ItemIndicator>
                </span>
                <S.ItemText>{o.label}</S.ItemText>
                {o.description ? <span className="ml-auto pl-3 text-sm text-muted">{o.description}</span> : null}
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}

/* ----------------------------------------------------------------- Combobox */

export interface ComboboxProps {
  options: Option[];
  value?: string | null;
  onValueChange: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  /** When set, filtering is done by the caller (server-side search) instead of in the browser. */
  onSearchChange?: (query: string) => void;
  clearable?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
  renderOption?: (option: Option) => ReactNode;
  /**
   * A row pinned under the list that leaves the picker to make something new ("Add a sport…"), so what is missing can be added right where
   * it is being chosen. It closes the picker and calls `onSelect`, which usually opens a dialog and then picks what it made.
   */
  createAction?: { label: string; onSelect: () => void };
  /**
   * Search, then create: while what is typed matches no option exactly (case and spacing aside), a "Create “…”" row is offered at the end of the list, reachable by
   * the arrow keys and Enter like any option. It closes the picker and is called with the text typed; the caller makes the entry (or finds it, if the server
   * already had one under another spelling) and picks it. Leave it out for someone who may not add to the list.
   */
  onCreate?: (text: string) => void;
  /** The words of the create row; "Create “text”" when left out. */
  createLabel?: (text: string) => string;
  /** The picker's name when no field label names it (a picker in a table cell). */
  'aria-label'?: string;
}

const sameText = (a: string, b: string) => a.replace(/\s+/g, ' ').trim().toLowerCase() === b.replace(/\s+/g, ' ').trim().toLowerCase();

/** Searchable dropdown - athlete pickers, long lists, anything where typing beats scrolling. With `onCreate`, what is not there can be added where it is chosen. */
export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  loading,
  onSearchChange,
  clearable,
  disabled,
  invalid,
  id,
  className,
  renderOption,
  createAction,
  onCreate,
  createLabel,
  'aria-label': ariaLabel,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const control = useControlProps({ id, invalid });
  const selected = options.find((o) => o.value === value);
  const typed = query.replace(/\s+/g, ' ').trim();
  const canCreate = !!onCreate && typed !== '' && !options.some((o) => sameText(o.label, typed));

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          id={control.id}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-describedby={control['aria-describedby']}
          aria-invalid={control['aria-invalid']}
          aria-required={control['aria-required']}
          disabled={disabled}
          data-placeholder={selected ? undefined : ''}
          className={cn(triggerBase, control.invalid && controlInvalid, className)}
        >
          <span className="min-w-0 flex-1 truncate">{selected ? (renderOption ? renderOption(selected) : selected.label) : placeholder}</span>
          {clearable && selected ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onValueChange(null);
              }}
              className="grid size-5 place-items-center rounded text-subtle hover:bg-sunken hover:text-fg"
            >
              <X className="size-3.5" aria-hidden />
            </span>
          ) : null}
          <ChevronsUpDown className="size-4 shrink-0 text-subtle" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0">
        <Command shouldFilter={!onSearchChange} loop>
          <div className="flex items-center border-b px-3">
            <Command.Input
              placeholder={searchPlaceholder}
              value={query}
              onValueChange={(text) => {
                setQuery(text);
                onSearchChange?.(text);
              }}
              className="h-10 flex-1 bg-transparent text-base text-fg outline-none placeholder:text-subtle"
            />
            {loading ? <Loader2 className="size-4 animate-spin text-subtle" aria-label="Loading" /> : null}
          </div>
          <Command.List className="max-h-64 overflow-y-auto p-1">
            {canCreate ? null : <Command.Empty className="px-3 py-6 text-center text-sm text-muted">{emptyText}</Command.Empty>}
            {options.map((o) => (
              <Command.Item
                key={o.value}
                value={`${o.label} ${o.description ?? ''} ${o.value}`}
                disabled={o.disabled}
                onSelect={() => {
                  onValueChange(o.value);
                  setOpen(false);
                }}
                className="flex min-h-9 cursor-default items-center gap-2.5 rounded-md px-2.5 py-1.5 text-base text-fg outline-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-sunken"
              >
                {renderOption ? (
                  renderOption(o)
                ) : (
                  <>
                    {o.icon}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{o.label}</span>
                      {o.description ? <span className="block truncate text-sm text-muted">{o.description}</span> : null}
                    </span>
                  </>
                )}
                {o.value === value ? <Check className="size-4 shrink-0 text-brand-text" aria-hidden /> : null}
              </Command.Item>
            ))}
            {canCreate ? (
              <Command.Item
                forceMount
                value={`__create__ ${typed}`}
                onSelect={() => {
                  setOpen(false);
                  setQuery('');
                  onCreate!(typed);
                }}
                className="flex min-h-9 cursor-default items-center gap-2.5 rounded-md px-2.5 py-1.5 text-base font-medium text-brand-text outline-none data-[selected=true]:bg-sunken"
              >
                <Plus className="size-4 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{createLabel ? createLabel(typed) : `Create “${typed}”`}</span>
              </Command.Item>
            ) : null}
          </Command.List>
        </Command>
        {createAction ? (
          <div className="border-t p-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                createAction.onSelect();
              }}
              className="flex min-h-9 w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-base font-medium text-brand-text outline-none hover:bg-sunken focus-visible:bg-sunken"
            >
              <Plus className="size-4 shrink-0" aria-hidden />
              {createAction.label}
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------- FilterSelect */

export interface FilterSelectProps {
  label: string;
  options: Option[];
  /** Selected values. For single mode use a 0/1 element array. */
  values: string[];
  onValuesChange: (values: string[]) => void;
  multiple?: boolean;
  className?: string;
}

/** Compact filter chip with a checklist popover: "Availability: Restricted +1". */
export function FilterSelect({ label, options, values, onValuesChange, multiple = true, className }: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const active = values.length > 0;
  const first = options.find((o) => o.value === values[0])?.label;
  const summary = active ? `${first}${values.length > 1 ? ` +${values.length - 1}` : ''}` : null;

  function toggle(v: string) {
    if (multiple) onValuesChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
    else {
      onValuesChange(values[0] === v ? [] : [v]);
      setOpen(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            'inline-flex h-(--control-h-sm) items-center gap-1.5 rounded-md border px-2.5 text-sm font-medium transition-colors',
            'min-h-8',
            active
              ? 'border-brand-border bg-brand-subtle text-brand-text hover:bg-brand-subtle-hover'
              : 'border-border-strong bg-surface text-muted hover:bg-surface-2 hover:text-fg',
            className,
          )}
        >
          <span>{label}</span>
          {summary ? <span className="font-semibold">: {summary}</span> : null}
          <ChevronDown className="size-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="min-w-56" align="start">
        <div role="listbox" aria-label={label} aria-multiselectable={multiple || undefined}>
          {options.map((o) => {
            const on = values.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => toggle(o.value)}
                className="flex min-h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-base text-fg hover:bg-sunken"
              >
                <span
                  aria-hidden
                  className={cn(
                    'grid size-4 shrink-0 place-items-center border',
                    multiple ? 'rounded-[4px]' : 'rounded-full',
                    on ? 'border-brand bg-brand text-on-brand' : 'border-input bg-surface',
                  )}
                >
                  {on ? <Check className="size-3" /> : null}
                </span>
                <span className="flex-1 truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
        {active ? (
          <div className="mt-1 border-t pt-1">
            <button
              type="button"
              onClick={() => onValuesChange([])}
              className="h-8 w-full rounded-md px-2.5 text-left text-sm font-medium text-brand-text hover:bg-brand-subtle"
            >
              Clear
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
